// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Deploy freshness — the ONE owner of "is this tab running a superseded
 * bundle?" (acceptance 4, 2026-09-04).
 *
 * Proven failure: a single-page app never reloads its JavaScript by itself.
 * `index.html` is `no-store`, which only helps on a reload — the owner's tab
 * kept executing the previous bundle for 26 minutes after a deploy and sent
 * the OLD Amy prefill. Nothing could show it.
 *
 * Mechanism: `scripts/deploy-frontend.py` bakes `VITE_BUILD_ID` into the
 * bundle (lib/buildInfo.ts) and writes the same id as `build_id` into the
 * no-store `/aws-exports.json`. This module fetches that id and compares it
 * with the executing bundle's. It NEVER reloads on its own — the shell
 * decides (a calm banner with a Reload button; the CTA path reloads
 * before handing a prefill to Amy, see components/layout/AppShell.tsx).
 *
 * Pure parts (`isStale`, `shouldCheck`) are unit-tested without React; the
 * hook adds mount / visibility / focus triggers, throttled to one fetch per
 * `FRESHNESS_MIN_INTERVAL_MS`. No polling timers.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { CLIENT_BUILD_ID, DEV_BUILD_ID } from "./buildInfo"

/** Minimum spacing between two passive checks (mount / visible / focus). */
export const FRESHNESS_MIN_INTERVAL_MS = 60_000

/** Where the deployed build id lives (served no-store by Amplify). */
export const DEPLOYED_BUILD_URL = "/aws-exports.json"

/**
 * GET /aws-exports.json (no-store) → its `build_id`, or null when the file,
 * the field or the network is unavailable. Never throws: an unknown deployed
 * id must degrade to "not stale", never to a broken shell.
 */
export async function fetchDeployedBuildId(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const response = await fetchImpl(DEPLOYED_BUILD_URL, { cache: "no-store" })
    if (!response.ok) return null
    const body: unknown = await response.json()
    if (typeof body !== "object" || body === null) return null
    const id = (body as { build_id?: unknown }).build_id
    return typeof id === "string" ? id : null
  } catch {
    return null
  }
}

/**
 * Stale = a deployed id is KNOWN (non-null, non-empty) and differs from the
 * bundle executing this code. Unknown/empty ids never count as stale.
 */
export function isStale(deployed: string | null, current: string): boolean {
  return deployed !== null && deployed !== "" && deployed !== current
}

/** Throttle rule for passive checks: first check always; then ≥ minInterval apart. */
export function shouldCheck(
  lastCheckedAt: number | null,
  now: number,
  minIntervalMs: number = FRESHNESS_MIN_INTERVAL_MS
): boolean {
  return lastCheckedAt === null || now - lastCheckedAt >= minIntervalMs
}

export interface DeployFreshnessOptions {
  /** Injectable for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch
  /** Injectable clock for the throttle. */
  now?: () => number
  /** The executing bundle's id; defaults to CLIENT_BUILD_ID. */
  currentBuildId?: string
  minIntervalMs?: number
  /** Event target for visibilitychange/focus (defaults to document/window). */
  doc?: Document
  win?: Window
}

export interface DeployFreshnessCheck {
  stale: boolean
  deployedBuildId: string | null
}

export interface DeployFreshnessState extends DeployFreshnessCheck {
  /**
   * Forces a fresh comparison (not throttled) and resolves with the result,
   * so a caller can act on it without waiting for a re-render. Used by the
   * reload-before-prefill path. Resolves `{ stale: false, … }` in dev builds.
   */
  checkNow: () => Promise<DeployFreshnessCheck>
}

/**
 * React owner of the freshness state. Checks on mount, when the tab becomes
 * visible and on window focus (throttled). Disabled entirely for the "dev"
 * build id — the aws-exports.json on a developer's disk may carry the last
 * DEPLOY's id, which would make every local session look stale.
 */
export function useDeployFreshness(options: DeployFreshnessOptions = {}): DeployFreshnessState {
  const {
    fetchImpl,
    now = () => Date.now(),
    currentBuildId = CLIENT_BUILD_ID,
    minIntervalMs = FRESHNESS_MIN_INTERVAL_MS,
    doc = typeof document === "undefined" ? undefined : document,
    win = typeof window === "undefined" ? undefined : window,
  } = options
  const enabled = currentBuildId !== DEV_BUILD_ID
  const [deployedBuildId, setDeployedBuildId] = useState<string | null>(null)
  const lastCheckedAtRef = useRef<number | null>(null)
  const inFlightRef = useRef<Promise<DeployFreshnessCheck> | null>(null)
  const mountedRef = useRef(true)
  // Latest injected fetch without re-subscribing the listeners on every render.
  const fetchRef = useRef(fetchImpl)
  fetchRef.current = fetchImpl

  const checkNow = useCallback((): Promise<DeployFreshnessCheck> => {
    if (!enabled) return Promise.resolve({ stale: false, deployedBuildId: null })
    if (inFlightRef.current) return inFlightRef.current
    lastCheckedAtRef.current = now()
    const run = fetchDeployedBuildId(fetchRef.current ?? fetch).then(deployed => {
      inFlightRef.current = null
      if (mountedRef.current) setDeployedBuildId(deployed)
      return { stale: isStale(deployed, currentBuildId), deployedBuildId: deployed }
    })
    inFlightRef.current = run
    return run
  }, [enabled, now, currentBuildId])

  useEffect(() => {
    mountedRef.current = true
    if (!enabled) return
    const passive = () => {
      if (doc && doc.visibilityState === "hidden") return
      if (shouldCheck(lastCheckedAtRef.current, now(), minIntervalMs)) void checkNow()
    }
    passive()
    doc?.addEventListener("visibilitychange", passive)
    win?.addEventListener("focus", passive)
    return () => {
      mountedRef.current = false
      doc?.removeEventListener("visibilitychange", passive)
      win?.removeEventListener("focus", passive)
    }
  }, [enabled, doc, win, now, minIntervalMs, checkNow])

  return { stale: isStale(deployedBuildId, currentBuildId), deployedBuildId, checkNow }
}
